import { Link } from 'react-router-dom'
import { Newspaper, Clock, ArrowRight, CalendarClock } from 'lucide-react'
import type { Page } from '../../api/types'

function fmt(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  })
}

// ── Single page row ───────────────────────────────────────────────────────────

function PageRow({
  page,
  loading,
  emptyMessage,
  variant,
}: {
  page: Page | undefined
  loading: boolean
  emptyMessage: string
  variant: 'live' | 'scheduled'
}) {
  const isScheduled = variant === 'scheduled'
  const blockCount = page?.blocks?.length ?? 0

  return (
    <div className={`flex flex-col px-5 py-4 ${isScheduled ? 'bg-info-fill/40' : ''}`}>
      {/* Row label + status badge */}
      <div className="mb-2 flex items-center gap-2">
        {isScheduled
          ? <CalendarClock size={12} className="text-info-fg" />
          : <Newspaper size={12} className="text-fg-secondary" />
        }
        <span className="font-sans text-[9px] font-bold uppercase tracking-widest text-fg-secondary">
          {isScheduled ? 'Next Scheduled' : 'Live'}
        </span>
        {page && (
          <span className={`ml-auto rounded-full border px-1.5 py-px font-sans text-[8px] font-bold uppercase tracking-wide ${
            isScheduled
              ? 'border-info-line text-info-fg'
              : 'border-success-line text-success-fg'
          }`}>
            {page.status}
          </span>
        )}
      </div>

      {/* Skeleton */}
      {loading && (
        <div className="space-y-2">
          <div className="h-4 w-3/4 animate-pulse rounded bg-fill" />
          <div className="h-3 w-1/2 animate-pulse rounded bg-fill" />
        </div>
      )}

      {/* Empty */}
      {!loading && !page && (
        <p className="font-sans text-xs text-fg-secondary">{emptyMessage}</p>
      )}

      {/* Content */}
      {!loading && page && (
        <>
          <p className="font-zodiak text-sm font-bold leading-snug text-fg">{page.title}</p>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="font-sans text-[11px] text-fg-secondary">{page.slug}</span>
            <span className="font-sans text-[11px] text-fg-secondary">
              {blockCount} block{blockCount !== 1 ? 's' : ''}
            </span>
            {page.scheduledFor && (
              <span className="flex items-center gap-1 font-sans text-[11px] text-info-fg">
                <Clock size={10} />{fmt(page.scheduledFor)}
              </span>
            )}
            {page.publishedAt && !page.scheduledFor && (
              <span className="flex items-center gap-1 font-sans text-[11px] text-fg-secondary">
                <Clock size={10} />Published {fmt(page.publishedAt)}
              </span>
            )}
          </div>
          <Link
            to={`/admin/pages/${page.id}`}
            className="mt-2 flex items-center gap-1 font-sans text-[11px] font-semibold text-accent-primary-fg hover:underline"
          >
            Edit in Production Desk <ArrowRight size={10} />
          </Link>
        </>
      )}
    </div>
  )
}

// ── Combined summary card ─────────────────────────────────────────────────────

export interface FrontPageSummaryCardProps {
  livePage: Page | undefined
  scheduledPage: Page | undefined
  liveLoading: boolean
  scheduledLoading: boolean
  embedded?: boolean
}

export function FrontPageSummaryCard({
  livePage,
  scheduledPage,
  liveLoading,
  scheduledLoading,
  embedded = false,
}: FrontPageSummaryCardProps) {
  const body = (
    <>
      <PageRow
        page={livePage}
        loading={liveLoading}
        emptyMessage="No front page is currently live."
        variant="live"
      />

      <div className="border-t border-line" />

      <PageRow
        page={scheduledPage}
        loading={scheduledLoading}
        emptyMessage="No front page is scheduled."
        variant="scheduled"
      />
    </>
  )

  if (embedded) {
    return (
      <div className="divide-y divide-line">
        {body}
      </div>
    )
  }

  return (
    <section className="flex flex-col rounded border border-line bg-surface">
      {/* Header */}
      <div className="flex items-center gap-2 border-b border-line px-5 py-3">
        <Newspaper size={14} className="text-fg-secondary" />
        <span className="font-sans text-[10px] font-bold uppercase tracking-widest text-fg-secondary">
          Front Pages
        </span>
      </div>

      {body}
    </section>
  )
}
